import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import GetAppIcon from '@material-ui/icons/GetApp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import VisibilityIcon from '@material-ui/icons/Visibility'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  createIncidentReport,
  updateIncidentReport,
  deleteIncidentReport,
  fetchScopedCentres,
  fetchIncidentReports,
  fetchUnderageOptions
} from 'api'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import { navigationPaths } from 'app/config'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

import { PROGRAMS, programForClass } from './programs'

import {
  downloadIncidentPdf,
  incidentPdfUrl
} from '../components/safeguardingPdf'
import SignaturePad from '../components/SignaturePad'

const ALL_CENTRES = 'all'
const CENTRE_KEY = 'oh.incidentReports.centre'

// What each table column shows, for the search boxes in its heading.
const INCIDENT_SEARCH = {
  child: (r) =>
    [r.child_name, r.program, r.data && (r.data.grade || r.data.program_typed)]
      .filter(Boolean)
      .join(' '),
  parent: (r) => [r.parent_name, r.parent_phone].filter(Boolean).join(' '),
  centre: (r) => r.centre_name,
  when: (r) => fmt(r.occurred_at),
  location: (r) => r.location,
  firstAid: (r) => (r.first_aid_given ? 'yes' : 'no'),
  told: (r) => (r.parents_notified ? 'yes' : 'no'),
  by: (r) => r.logged_by,
  status: (r) => (r.submitted ? 'submitted' : 'draft')
}

const useStyles = makeStyles((theme) => ({
  // Match Newsletter / Event Creative exactly: padded root, plain table,
  // no card box. The new tabs looked like a different product without it.
  root: { padding: theme.spacing(3) },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  filters: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  centre: { minWidth: 200 },
  // No overflow box and no fixed minimum: the table fits the window (cells
  // wrap) and its heading sticks to the top of the window on scroll.
  table: { width: '100%' },
  empty: { padding: theme.spacing(4), textAlign: 'center' },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600
  },
  sigs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2)
  },
  // The live parent-search sits directly under its box, like any type-ahead —
  // a thin bordered list, each row a whole clickable target. Copied from the
  // Underage Consent form so the two safeguarding tabs feel like one product.
  searchWrap: { position: 'relative' },
  results: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    marginTop: 4,
    overflow: 'hidden'
  },
  result: {
    padding: '8px 12px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: '#f5f7ff' }
  },
  resultMeta: { color: theme.palette.text.secondary },
  parentLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' }
  },
  chipSubmitted: {
    background: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12
  },
  chipDraft: {
    background: '#f5f5f5',
    color: '#616161',
    borderRadius: 12,
    padding: '2px 10px',
    fontSize: 12
  }
}))

const BLANK = {
  centre: '',
  child_name: '',
  program: '',
  // The parent the report is filed against. `parent` is their uuid when the
  // search found them in Alfred (that is what the list links to); the name and
  // number are kept as text either way, because a walk-in family has no uuid and
  // the report still has to say who was contacted.
  parent: '',
  parent_name: '',
  parent_phone: '',
  // Date and time are two separate fields, not one datetime-local: Safari on
  // Mac cannot pick the time on a datetime-local input, so the team could set
  // the date but never the time.
  occurred_date: '',
  occurred_time: '',
  location: '',
  first_aid_given: false,
  parents_notified: false,
  educator_signature: '',
  director_signature: '',
  data: {
    grade: '',
    what_happened: '',
    what_we_did: '',
    educator_names: '',
    director_name: '',
    how_parent_informed: '',
    parent_response: ''
  }
}

const fmt = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
}

const IncidentReports = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // HQ (or anyone not mapped to a centre) keeps the "all centres" option; a
  // centre-mapped user has nothing for it to mean.
  const [unrestricted, setUnrestricted] = useState(true)
  const [centreUuid, setCentreUuid] = useState(
    () => window.localStorage.getItem(CENTRE_KEY) || ALL_CENTRES
  )
  const [rows, setRows] = useState([])
  const colSearch = useColumnSearch(rows, INCIDENT_SEARCH)
  const [loading, setLoading] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)
  // The uuid of the DRAFT being edited, or null for a new report. A submitted
  // report is never editable — that is the point of submitting it.
  const [editingUuid, setEditingUuid] = useState(null)

  // Live search: the team types the PARENT's name or registered number and the
  // five best matches appear under the box — pick one and the report is filed
  // against that parent, so the list can link to their profile. The parent is
  // what the centre reliably has on record; the child may be a new joiner, or
  // spelt three different ways across the sheets.
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  // A pick writes the chosen name back into the box; this stops that write from
  // immediately firing a fresh search for the name we just chose.
  const skipSearch = useRef(false)
  // True once a parent has been chosen from the list. It suppresses the
  // "no matching parent" hint — that line means "we searched and found nobody",
  // and after a successful pick the box holds the chosen name with no results
  // showing, which would otherwise trip the hint even though a parent WAS found.
  // Typing in the box again clears it, because the team is searching afresh.
  const [parentPicked, setParentPicked] = useState(false)
  // The chosen parent's children, straight from Alfred. One child fills the
  // name and programme on its own; several offer a picker so the team says which
  // one the report is about. Empty for a family Alfred has never seen — then the
  // child's name is simply typed, exactly as before.
  const [childOptions, setChildOptions] = useState([])

  const [viewRow, setViewRow] = useState(null)
  const [pdfUrl, setPdfUrl] = useState('')
  const [sopOpen, setSopOpen] = useState(false)

  useEffect(() => {
    let active = true
    const run = async () => {
      try {
        // Only the centres this user may actually read. Somebody mapped to one
        // centre gets one centre — the API scopes the reports the same way, so a
        // dropdown built from every centre would just offer empty lists.
        const data = await fetchScopedCentres()
        const list = data?.centres || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) => {
          const allowed =
            prev &&
            ((prev === ALL_CENTRES && data?.unrestricted) ||
              list.some((c) => c.uuid === prev))
          if (allowed) return prev
          // A centre-mapped user has no "all centres" to fall back to; land them
          // on their own centre instead of an option that is not theirs.
          return data?.unrestricted ? ALL_CENTRES : list[0]?.uuid || ''
        })
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      const data = await fetchIncidentReports(centreUuid)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      showToast('Could not load incident reports', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [centreUuid])

  useEffect(() => {
    load()
    if (centreUuid) window.localStorage.setItem(CENTRE_KEY, centreUuid)
  }, [centreUuid, load])

  const openForm = () => {
    setSearch('')
    setResults([])
    setParentPicked(false)
    setChildOptions([])
    setEditingUuid(null)
    // The incident is nearly always written up the same day, so default the
    // date and time to now (in the team's OWN timezone — not UTC) and let them
    // correct it. An empty date/time is a field to fill on every report for no
    // reason.
    const pad = (n) => String(n).padStart(2, '0')
    const now = new Date()
    setForm({
      ...BLANK,
      data: { ...BLANK.data },
      centre: centreUuid !== ALL_CENTRES ? centreUuid : '',
      occurred_date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate()
      )}`,
      occurred_time: `${pad(now.getHours())}:${pad(now.getMinutes())}`
    })
    setFormOpen(true)
  }

  /* Reopen a DRAFT in the same form. Only a draft can come back — a submitted
     report is the record and the backend refuses to change it. The stored
     instant is split back into the local date + time the two inputs expect. */
  const openEdit = (row) => {
    const pad = (n) => String(n).padStart(2, '0')
    let occurredDate = ''
    let occurredTime = ''
    if (row.occurred_at) {
      const dt = new Date(row.occurred_at)
      if (!Number.isNaN(dt.getTime())) {
        occurredDate = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
          dt.getDate()
        )}`
        occurredTime = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
      }
    }
    setSearch('')
    setResults([])
    setParentPicked(false)
    setChildOptions([])
    setEditingUuid(row.uuid)
    setForm({
      ...BLANK,
      centre: row.centre || '',
      child_name: row.child_name || '',
      program: row.program || '',
      parent: row.parent || '',
      parent_name: row.parent_name || '',
      parent_phone: row.parent_phone || '',
      occurred_date: occurredDate,
      occurred_time: occurredTime,
      location: row.location || '',
      first_aid_given: Boolean(row.first_aid_given),
      parents_notified: Boolean(row.parents_notified),
      educator_signature: row.educator_signature || '',
      director_signature: row.director_signature || '',
      data: { ...BLANK.data, ...(row.data || {}) }
    })
    setFormOpen(true)
  }

  const setField = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: v }))
  }

  const setData = (key) => (e) => {
    const v = e.target.value
    setForm((f) => ({ ...f, data: { ...f.data, [key]: v } }))
  }

  /* Search-as-you-type by name OR number over PARENTS, reusing the same endpoint
     the Underage Consent form uses. Picking one files the report against a real
     Alfred parent, which is what lets the list link to their profile. Debounced
     so a keystroke is not a request. */
  useEffect(() => {
    if (!formOpen) return undefined
    if (skipSearch.current) {
      skipSearch.current = false
      setResults([])
      setSearching(false)
      return undefined
    }
    const q = (search || '').trim()
    if (q.length < 2) {
      setResults([])
      setSearching(false)
      return undefined
    }
    let active = true
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const opts = await fetchUnderageOptions(undefined, undefined, q)
        if (active) setResults(opts?.parents || [])
      } catch (e) {
        if (active) setResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 300)
    return () => {
      active = false
      clearTimeout(handle)
    }
  }, [search, formOpen])

  const pickParent = (p) => {
    // Injecting the name back into the box would otherwise trigger a new search
    // for the parent we just chose — skip that one round.
    skipSearch.current = true
    setSearch(p.parent_name || '')
    setResults([])
    setParentPicked(true)
    const kids = Array.isArray(p.children) ? p.children : []
    setChildOptions(kids)
    // One child fills its own name and programme; a family with several leaves
    // both blank for the picker below to set, so the report is never silently
    // filed against the wrong sibling.
    const onlyChild = kids.length === 1 ? kids[0] : null
    setForm((f) => ({
      ...f,
      parent: p.parent || '',
      parent_name: p.parent_name || f.parent_name,
      parent_phone: p.parent_phone || f.parent_phone,
      // Only when the team has not already chosen one. An apartment parent has
      // no centre preference at all, and then whatever the team picked stands.
      centre: f.centre || p.centre || '',
      child_name: onlyChild ? onlyChild.child_name || '' : '',
      program: onlyChild ? programForClass(onlyChild.program) : '',
      // The child's class itself (Nursery, K1…) — the program list is broader.
      data: { ...f.data, grade: onlyChild ? onlyChild.program || '' : '' }
    }))
  }

  // The team picks which child of the chosen parent the report is about; the
  // name and programme follow from it. They can still edit the name by hand
  // afterwards — the picker only saves them the typing, it does not lock it.
  const pickChild = (student) => {
    const c = childOptions.find((k) => k.student === student)
    if (!c) return
    setForm((f) => ({
      ...f,
      child_name: c.child_name || '',
      program: programForClass(c.program) || f.program,
      data: { ...f.data, grade: c.program || f.data.grade || '' }
    }))
  }

  // A report is a safeguarding record, so SUBMIT demands the whole form. The two
  // parent-follow-up details are required only once "parents were notified" is
  // ticked — asking how a parent was informed when they were not yet informed is
  // nonsense. Everything still-missing is named back to the user so they know
  // exactly what stands between them and submitting.
  const missingForSubmit = () => {
    const d = form.data || {}
    const miss = []
    if (!form.centre) miss.push('centre')
    if (!form.child_name.trim()) miss.push("child's name")
    if (!form.program.trim()) miss.push('program')
    if (!form.occurred_date) miss.push('date')
    if (!form.occurred_time) miss.push('time')
    if (!form.location.trim()) miss.push('location')
    if (!(d.what_happened || '').trim()) miss.push('what happened')
    if (!(d.what_we_did || '').trim()) miss.push('what we did')
    if (!(d.educator_names || '').trim()) miss.push('educator name(s)')
    if (!form.educator_signature) miss.push('educator signature')
    if (!form.director_signature) miss.push('director signature')
    if (form.parents_notified) {
      if (!(d.how_parent_informed || '').trim()) {
        miss.push('how the parent was informed')
      }
      if (!(d.parent_response || '').trim()) miss.push('what the parent said')
    }
    return miss
  }

  const missing = missingForSubmit()
  const canSubmit = missing.length === 0
  // A draft is the home for an incomplete report, so it asks for almost nothing —
  // just enough to show up in the list as a real, findable row.
  const canSaveDraft = Boolean(form.centre && form.child_name.trim())

  const save = async (submit) => {
    if (submit && !canSubmit) {
      showToast(
        `All fields are required to submit. Still needed: ${missing.join(
          ', '
        )}`,
        constants.ERROR
      )
      return
    }
    if (!submit && !canSaveDraft) {
      showToast(
        'A draft needs at least a centre and the child’s name',
        constants.ERROR
      )
      return
    }
    setSaving(true)
    try {
      // Recombine the two fields into the single instant the API stores. When a
      // draft has only the date, keep it at midnight rather than losing it.
      const occurredAt = form.occurred_date
        ? new Date(
            `${form.occurred_date}T${form.occurred_time || '00:00'}`
          ).toISOString()
        : null
      const payload = {
        ...form,
        occurred_at: occurredAt,
        // No parent picked = no link to make. Send null, not '' — an empty
        // string is not a uuid and the API would reject the whole report over a
        // field the team was never required to fill.
        parent: form.parent || null,
        submitted: submit
      }
      if (editingUuid) {
        await updateIncidentReport(editingUuid, payload)
      } else {
        await createIncidentReport(payload)
      }
      showToast(
        submit
          ? 'Incident report submitted'
          : `Draft ${editingUuid ? 'updated' : 'saved'}`,
        constants.SUCCESS
      )
      setFormOpen(false)
      setEditingUuid(null)
      load()
    } catch (e) {
      showToast(
        e?.response?.status === 409
          ? 'A submitted report cannot be edited'
          : 'Could not save the report',
        constants.ERROR
      )
    } finally {
      setSaving(false)
    }
  }

  const openPdf = async (row) => {
    // Preview the DOCUMENT, not a re-rendered summary of it: if the two can
    // differ, the one you checked is not the one that prints. The builder is
    // async (it lazy-loads the brand fonts/artwork), so await the blob URL.
    setViewRow(row)
    setPdfUrl(await incidentPdfUrl(row))
  }

  const closePdf = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    setPdfUrl('')
    setViewRow(null)
  }

  const remove = async (row) => {
    try {
      await deleteIncidentReport(row.uuid)
      showToast('Draft deleted', constants.SUCCESS)
      load()
    } catch (e) {
      showToast(
        e?.response?.status === 409
          ? 'A submitted report cannot be deleted'
          : 'Could not delete',
        constants.ERROR
      )
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.head}>
        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="h5">Incident Reports</Typography>
            <Tooltip title="how this works">
              <IconButton size="small" onClick={() => setSopOpen(true)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className={classes.filters}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.centre}
          >
            <InputLabel>centre</InputLabel>
            <Select
              label="centre"
              value={centreUuid}
              onChange={(e) => setCentreUuid(e.target.value)}
            >
              {unrestricted && (
                <MenuItem value={ALL_CENTRES}>all centres</MenuItem>
              )}
              {centres.map((c) => (
                <MenuItem key={c.uuid} value={c.uuid}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openForm}
          >
            new report
          </Button>
        </Box>
      </Box>

      <Box>
        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Box className={classes.empty}>
            <Typography variant="body2" color="textSecondary">
              no incident reports yet.
            </Typography>
          </Box>
        ) : (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <SearchHeadCell label="child" search={colSearch.box('child')} />
                <SearchHeadCell
                  label="parent"
                  search={colSearch.box('parent')}
                />
                <SearchHeadCell
                  label="centre"
                  search={colSearch.box('centre')}
                />
                <SearchHeadCell label="when" search={colSearch.box('when')} />
                <SearchHeadCell
                  label="location"
                  search={colSearch.box('location')}
                />
                <SearchHeadCell
                  label="first aid"
                  search={colSearch.box('firstAid')}
                />
                <SearchHeadCell
                  label="parents told"
                  search={colSearch.box('told')}
                />
                <SearchHeadCell
                  label="logged by"
                  search={colSearch.box('by')}
                />
                <SearchHeadCell
                  label="status"
                  search={colSearch.box('status')}
                />
                <SearchHeadCell label="actions" align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {!colSearch.rows.length && (
                <TableRow>
                  <TableCell colSpan={10} className={classes.empty}>
                    no reports match the search.
                  </TableCell>
                </TableRow>
              )}
              {colSearch.rows.map((r) => (
                <TableRow key={r.uuid} hover>
                  <TableCell>
                    <b>{r.child_name}</b>
                    {r.program ? (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {[
                          r.program,
                          r.data && (r.data.grade || r.data.program_typed)
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {/* A link only when the report is tied to a real Alfred
                        parent. A typed-in name has no profile to open, and a
                        link that goes nowhere is worse than plain text. */}
                    {r.parent ? (
                      <a
                        href={`${navigationPaths.parentdashboard}?parent=${r.parent}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.parentLink}
                      >
                        {r.parent_name || 'parent'}
                      </a>
                    ) : (
                      r.parent_name || '—'
                    )}
                    {r.parent_phone ? (
                      <Typography
                        variant="caption"
                        display="block"
                        color="textSecondary"
                      >
                        {r.parent_phone}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.centre_name}</TableCell>
                  <TableCell>{fmt(r.occurred_at)}</TableCell>
                  <TableCell>{r.location || '—'}</TableCell>
                  <TableCell>{r.first_aid_given ? 'yes' : 'no'}</TableCell>
                  <TableCell>{r.parents_notified ? 'yes' : 'no'}</TableCell>
                  <TableCell>{r.logged_by || '—'}</TableCell>
                  <TableCell>
                    <span
                      className={
                        r.submitted ? classes.chipSubmitted : classes.chipDraft
                      }
                    >
                      {r.submitted ? 'submitted' : 'draft'}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="view">
                      <IconButton size="small" onClick={() => openPdf(r)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="download pdf">
                      <IconButton
                        size="small"
                        onClick={() => downloadIncidentPdf(r)}
                      >
                        <GetAppIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!r.submitted ? (
                      <Tooltip title="edit draft">
                        <IconButton size="small" onClick={() => openEdit(r)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {!r.submitted ? (
                      <Tooltip title="delete draft">
                        <IconButton size="small" onClick={() => remove(r)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* the form */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUuid ? 'edit draft' : 'new incident report'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            search the parent by name or registered number — the report is then
            filed against them, and the list links to their profile.
          </Typography>

          <Box className={classes.searchWrap} mb={1}>
            <TextField
              label="search parent by name or number"
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                // A fresh keystroke means the team is searching again, so the
                // earlier pick no longer stands — let the hint speak once more
                // if this new search finds nobody.
                setParentPicked(false)
              }}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searching ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SearchIcon fontSize="small" color="disabled" />
                    )}
                  </InputAdornment>
                )
              }}
            />
            {results.length > 0 ? (
              <Box className={classes.results}>
                {results.map((p) => (
                  <Box
                    key={p.parent}
                    className={classes.result}
                    onClick={() => pickParent(p)}
                  >
                    <Typography variant="body2">
                      <b>{p.parent_name}</b>
                      {p.parent_phone ? ` · ${p.parent_phone}` : ''}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.resultMeta}
                    >
                      {p.centre_name || 'no centre on record'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : search.trim().length >= 2 && !searching && !parentPicked ? (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: 'block', marginTop: 6 }}
              >
                no matching parent — type the name and number in below
              </Typography>
            ) : null}
          </Box>

          {/* Once a parent is chosen, their children in Alfred are shown as
              suggestions — always visible, so a single child is offered too and
              nobody has to wonder whether Alfred knew of it. Clicking one fills
              the name and programme below; the name stays editable, and a family
              Alfred does not know leaves it to the typed name. */}
          {childOptions.length > 0 ? (
            <Box mb={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: 'block', marginBottom: 4 }}
              >
                {childOptions.length > 1
                  ? 'this parent has more than one child — pick who the report is about'
                  : 'child on file for this parent — pick to fill, or edit the name below'}
              </Typography>
              <Box className={classes.results}>
                {childOptions.map((k) => {
                  const selected =
                    !!k.child_name && k.child_name === form.child_name
                  return (
                    <Box
                      key={k.student}
                      className={classes.result}
                      onClick={() => pickChild(k.student)}
                      style={selected ? { background: '#eef2ff' } : undefined}
                    >
                      <Typography variant="body2">
                        <b>{k.child_name}</b>
                        {k.program ? ` · ${k.program}` : ''}
                        {selected ? '  ✓' : ''}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>
          ) : null}

          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 220 }}
            >
              <InputLabel>centre *</InputLabel>
              <Select
                label="centre *"
                value={form.centre}
                onChange={setField('centre')}
              >
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="child's name *"
              variant="outlined"
              size="small"
              value={form.child_name}
              onChange={setField('child_name')}
              style={{ minWidth: 220 }}
            />
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 180 }}
            >
              <InputLabel>program *</InputLabel>
              <Select
                label="program *"
                value={form.program}
                onChange={setField('program')}
              >
                {/* An older report keeps the program that was typed for it. */}
                {(PROGRAMS.includes(form.program) || !form.program
                  ? PROGRAMS
                  : [form.program, ...PROGRAMS]
                ).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="grade / class"
              variant="outlined"
              size="small"
              value={(form.data && form.data.grade) || ''}
              onChange={setData('grade')}
              style={{ minWidth: 160 }}
            />
          </Box>

          {/* Filled by the search above, and typeable for a family Alfred has
              never seen. Editing either by hand drops the profile link — the
              name shown must be the parent the link goes to, or the list lies. */}
          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={2}>
            <TextField
              label="parent's name"
              variant="outlined"
              size="small"
              value={form.parent_name}
              onChange={(e) => {
                const { value } = e.target
                setForm((f) => ({ ...f, parent_name: value, parent: '' }))
              }}
              style={{ minWidth: 220 }}
            />
            <TextField
              label="parent's number"
              variant="outlined"
              size="small"
              value={form.parent_phone}
              onChange={(e) => {
                const { value } = e.target
                setForm((f) => ({ ...f, parent_phone: value, parent: '' }))
              }}
              style={{ minWidth: 180 }}
            />
          </Box>

          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={2}>
            <TextField
              label="date of incident *"
              type="date"
              variant="outlined"
              size="small"
              value={form.occurred_date}
              onChange={setField('occurred_date')}
              InputLabelProps={{ shrink: true }}
              style={{ minWidth: 180 }}
            />
            <TextField
              label="time of incident *"
              type="time"
              variant="outlined"
              size="small"
              value={form.occurred_time}
              onChange={setField('occurred_time')}
              InputLabelProps={{ shrink: true }}
              style={{ minWidth: 160 }}
            />
            <TextField
              label="location of the incident *"
              variant="outlined"
              size="small"
              value={form.location}
              onChange={setField('location')}
              style={{ minWidth: 240 }}
            />
          </Box>

          <Typography variant="subtitle2" className={classes.section}>
            what happened *
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder="brief description of the incident"
            value={form.data.what_happened}
            onChange={setData('what_happened')}
          />

          <Typography variant="subtitle2" className={classes.section}>
            action taken
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block">
            what the team did in response to the incident
          </Typography>

          <Box
            mt={1}
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 8 }}
          >
            <Typography variant="body2">was first aid given?</Typography>
            <RadioGroup
              row
              value={form.first_aid_given ? 'yes' : 'no'}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  first_aid_given: e.target.value === 'yes'
                }))
              }
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" size="small" />}
                label="yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio color="primary" size="small" />}
                label="no"
              />
            </RadioGroup>
          </Box>

          <Typography variant="subtitle2" className={classes.section}>
            what did we do? *
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.data.what_we_did}
            onChange={setData('what_we_did')}
          />

          <Typography variant="subtitle2" className={classes.section}>
            follow up
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            style={{ gap: 8 }}
          >
            <Typography variant="body2">were parents notified?</Typography>
            <RadioGroup
              row
              value={form.parents_notified ? 'yes' : 'no'}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  parents_notified: e.target.value === 'yes'
                }))
              }
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" size="small" />}
                label="yes"
              />
              <FormControlLabel
                value="no"
                control={<Radio color="primary" size="small" />}
                label="no"
              />
            </RadioGroup>
          </Box>
          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap" mt={1}>
            <TextField
              label={
                form.parents_notified
                  ? 'how did we inform the parent *'
                  : 'how did we inform the parent'
              }
              variant="outlined"
              size="small"
              value={form.data.how_parent_informed}
              onChange={setData('how_parent_informed')}
              style={{ flex: 1, minWidth: 240 }}
            />
            <TextField
              label={
                form.parents_notified
                  ? 'what did the parent say *'
                  : 'what did the parent say'
              }
              variant="outlined"
              size="small"
              value={form.data.parent_response}
              onChange={setData('parent_response')}
              style={{ flex: 1, minWidth: 240 }}
            />
          </Box>

          <Typography variant="subtitle2" className={classes.section}>
            signatures
          </Typography>
          {/* Educator on the left, centre director on the right — the same two
              columns the PDF prints, so each name sits with its own signature. */}
          <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
            <TextField
              label="name(s) of the educator(s) *"
              variant="outlined"
              size="small"
              value={form.data.educator_names}
              onChange={setData('educator_names')}
              style={{ flex: 1, minWidth: 240 }}
            />
            <TextField
              label="centre director name"
              variant="outlined"
              size="small"
              value={form.data.director_name}
              onChange={setData('director_name')}
              style={{ flex: 1, minWidth: 240 }}
            />
          </Box>
          <Box mt={2} className={classes.sigs}>
            <SignaturePad
              label="educator *"
              value={form.educator_signature}
              onChange={(v) =>
                setForm((f) => ({ ...f, educator_signature: v }))
              }
            />
            <SignaturePad
              label="centre director *"
              value={form.director_signature}
              onChange={(v) =>
                setForm((f) => ({ ...f, director_signature: v }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>cancel</Button>
          <Button
            onClick={() => save(false)}
            disabled={saving || !canSaveDraft}
          >
            save draft
          </Button>
          <Tooltip title={canSubmit ? '' : 'complete every field to submit'}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={() => save(true)}
                disabled={saving || !canSubmit}
              >
                {saving ? 'saving…' : 'submit report'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* The document itself. Previewing a re-typed summary would mean the
          thing you checked is not the thing that prints. */}
      <Dialog
        open={Boolean(viewRow)}
        onClose={closePdf}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{viewRow?.child_name} — incident report</DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="incident report"
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<GetAppIcon />}
            onClick={() => viewRow && downloadIncidentPdf(viewRow)}
          >
            download pdf
          </Button>
          <Button onClick={closePdf} color="primary">
            close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sopOpen}
        onClose={() => setSopOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>How Incident Reports work</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This is the Accident &amp; Incident Report (AIR). The educator or
            centre team fills it in — press <b>new report</b>, top right. Search
            the <b>parent</b> by name or number and pick them, so the report is
            filed against the family and the list links to their profile. Alfred
            then fills in the child; if the family has more than one, a picker
            asks which child the report is about. A family Alfred does not know
            yet can simply be typed in.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <b>Every field is required to submit.</b> If something is still
            missing, use <b>save draft</b> — the report is kept as a draft and
            can only be submitted once it is complete.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <b>A draft can be edited.</b> Use the pencil against any draft in
            the list to reopen it, change anything, and either save it as a
            draft again or submit it.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <b>A submitted report is the record.</b> It cannot be edited or
            deleted afterwards — that is the point of it. If something needs
            correcting, file a new report.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Sign with your finger or mouse. The educator and the centre director
            each sign separately, exactly as on the paper form.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSopOpen(false)} color="primary">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default IncidentReports
